import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { searchProducts } from '../utils/api';
import SectionHeading from '../components/SectionHeading';
import ProductGrid from '../components/ProductGrid';
import SortBar from '../components/SortBar';
import Pagination from '../components/Pagination';
import { useCart } from '../context/CartContext';

function useQueryParams() {
  const location = useLocation();
  const { search, pathname } = location;

  return useMemo(() => {
    const q = new URLSearchParams(search);
    const params = Object.fromEntries(q.entries());

    // map pathname to category slug
    const slug = (pathname || '').replace(/^\//, '').replace(/\.html$/, '');
    const categoryMap = [
      'men-shoes',
      'women-shoes',
      'kids-shoes',
      'sneakers',
      'sport-shoes',
      'boots',
      'official-shoes',
      'slip-on-shoes',
    ];
    if (categoryMap.includes(slug)) params.category = slug;

    params.page = parseInt(params.page || '1', 10);
    params.limit = parseInt(params.limit || '12', 10);
    return params;
  }, [search, pathname]);
}

export default function Category() {
  const params = useQueryParams();
  const [sort, setSort] = useState(params.sort || '');
  const { add } = useCart();

  // Keep previous data so pagination feels smooth
  const { data, isFetching } = useQuery({
    queryKey: ['products', { ...params, sort }],
    queryFn: () => searchProducts({ ...params, sort }),
    keepPreviousData: true,
  });

  const items = data?.items || [];
  const total = data?.total || 0;
  const page = data?.page || 1;
  const limit = data?.limit || 12;

  return (
    <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center justify-between">
        <SectionHeading
          title="Shop"
          subtitle={isFetching ? 'Loading…' : `${total} items`}
        />
        <SortBar
          sort={sort}
          onChange={(v) => {
            setSort(v);
            const qs = new URLSearchParams(window.location.search);
            if (v) qs.set('sort', v); else qs.delete('sort');
            qs.set('page', '1');
            window.history.replaceState(null, '', `?${qs.toString()}`);
          }}
        />
      </div>

      <ProductGrid items={items} onAdd={add} />

      <div className="mt-8">
        <Pagination
          page={page}
          total={total}
          limit={limit}
          onPage={(p) => {
            const qs = new URLSearchParams(window.location.search);
            qs.set('page', String(p));
            window.history.replaceState(null, '', `?${qs.toString()}`);
            // React Query will refetch automatically due to queryKey change
          }}
        />
      </div>
    </main>
  );
}
